import { useLoadScript, type Libraries } from "@react-google-maps/api";
import { COOKIE_LANG } from "@france/superelements/utils";

import { env } from "../../env";
import { getCookie } from "../security/cookies";

/**
 * Single entry point for loading the Google Maps JavaScript API.
 *
 * The API must be injected once per page: a second <script> makes Maps log
 * "You have included the Google Maps JavaScript API multiple times on this
 * page. This may cause unexpected errors." and can break the first instance.
 *
 * `useLoadScript` de-duplicates the injection as long as every caller passes
 * the same options — which is exactly why they live here rather than at each
 * call site. Components that render a map through `google-maps-react-markers`
 * must pass `loadScriptExternally` and forward `isLoaded` as its `status`, so
 * that library reuses this script instead of appending its own.
 */
const libraries: Libraries = ["places", "geometry"];

const useGoogleMaps = () => {
  // getCookie widens to string | object | null, so narrow before handing the
  // value to the loader rather than stringifying an object into the script URL.
  const language = getCookie(COOKIE_LANG);

  return useLoadScript({
    googleMapsApiKey: env.GOOGLE_API_KEY || "",
    libraries,
    language: typeof language === "string" ? language : "es",
    region: "es",
  });
};

export default useGoogleMaps;
